/**
 * SessionCard Component
 * Displays a single deep analysis session in a card format
 */

import { useTranslation } from 'react-i18next';
import { DeepAnalysisHistoryItem } from '../../services/api';
import { getStatusLabel, getStatusBadgeClasses } from './utils/sessionMapper';

interface SessionCardProps {
  session: DeepAnalysisHistoryItem;
  onClick: (sessionId: string) => void;
  isLoading?: boolean;
}

export function SessionCard({ session, onClick, isLoading }: SessionCardProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'pl' ? 'pl-PL' : 'en-US';
  const statusLabel = t(getStatusLabel(session.status as 'complete' | 'error' | 'clarification' | 'pending'));
  const statusClasses = getStatusBadgeClasses(session.status as 'complete' | 'error' | 'clarification' | 'pending');

  // Format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Format token count
  const formatTokens = (tokens: number) => {
    if (tokens >= 1000) {
      return `${(tokens / 1000).toFixed(1)}k`;
    }
    return String(tokens);
  };

  // Truncate preview text
  const truncatedPreview = session.documentPreview.length > 100
    ? session.documentPreview.substring(0, 100) + '...'
    : session.documentPreview;

  return (
    <button
      onClick={() => onClick(session.id)}
      disabled={isLoading}
      className={`w-full text-left p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg hover:border-purple-300 dark:hover:border-purple-700 hover:shadow-md transition-all ${
        isLoading ? 'opacity-50 cursor-wait' : 'cursor-pointer'
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`px-2 py-0.5 text-xs font-medium rounded ${statusClasses}`}>
            {statusLabel}
          </span>
          {session.isShared && (
            <span className="px-2 py-0.5 text-xs font-medium rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
              Udostepnione
            </span>
          )}
        </div>
        <span className="text-xs text-slate-400 dark:text-slate-500 whitespace-nowrap">
          {formatDate(session.createdAt)}
        </span>
      </div>

      {/* Preview text */}
      <p className="text-sm text-slate-700 dark:text-slate-300 line-clamp-2 mb-3">
        {truncatedPreview}
      </p>

      {/* Footer with stats */}
      <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
        {/* Tokens used */}
        <div className="flex items-center gap-1">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <span>{formatTokens(session.tokensTotal)} tokenów</span>
        </div>

        {/* Has report indicator */}
        {session.hasReport && (
          <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Raport</span>
          </div>
        )}
      </div>
    </button>
  );
}

export default SessionCard;
